/**
 * Pure policy for the "new tutorial available" prompt shown after login /
 * after a deploy. Kept free of React/Redux so it can be table-tested like
 * shouldOfferIosApp / decideAutoPausedPopup.
 *
 * Returns the first tutorial that should be offered, or null.
 */

export const TUTORIAL_STATUS = Object.freeze({
  COMPLETED: "COMPLETED",
  SKIPPED: "SKIPPED",
});

const PROMPT_ALLOWED_ROLES = new Set(["contributor", "manager", "admin"]);

// Routes where a tour prompt would be wrong or impossible (auth flows, e2e).
const isBlockedPath = (pathname) => {
  if (!pathname) return true;
  if (pathname.startsWith("/signin")) return true;
  if (pathname.startsWith("/resetpassword")) return true;
  if (pathname.startsWith("/passwordsent")) return true;
  if (pathname.startsWith("/__e2e__")) return true;
  if (pathname.startsWith("/privacy-policy")) return true;
  if (pathname.startsWith("/terms-of-use")) return true;
  return false;
};

/**
 * @param {object} args
 * @param {Array}  args.tutorials          registry entries
 * @param {object} args.userState          { byId: { [id]: { status, versionSeen } }, preferences: { suppressPrompts } }
 * @param {boolean} args.stateLoaded       false until persisted state has been read — never prompt before that
 * @param {boolean} args.sessionAlreadyShown once per tab session
 * @param {number}  args.uploadsInFlight   active uploads (UPLOADING/VERIFYING/PAUSED) — never distract these
 * @param {string}  args.pathname
 * @param {string}  args.role
 * @param {object}  [args.config]          admin config { hidden, noPrompt, mandatory }.
 *   hidden / noPrompt never prompt. `mandatory` tutorials must be COMPLETED:
 *   skipping only postpones them to the next session, and the user's
 *   "don't show tips" switch does not silence them.
 */
export const decideTutorialPrompt = ({
  tutorials = [],
  userState,
  stateLoaded = false,
  sessionAlreadyShown = false,
  uploadsInFlight = 0,
  pathname = "/",
  role = "",
  config = null,
} = {}) => {
  if (!stateLoaded) return null;
  if (sessionAlreadyShown) return null;
  if (uploadsInFlight > 0) return null;
  if (isBlockedPath(pathname)) return null;
  if (!PROMPT_ALLOWED_ROLES.has((role || "").toLowerCase())) return null;

  const byId = userState?.byId || {};
  const hidden = new Set(config?.hidden || []);
  const noPrompt = new Set(config?.noPrompt || []);
  const mandatory = new Set(config?.mandatory || []);
  const suppressed = !!userState?.preferences?.suppressPrompts;

  // Mandatory first: they are the only ones that survive "don't show tips",
  // and a SKIPPED record does not satisfy them — only COMPLETED does.
  for (const pass of ["mandatory", "optional"]) {
    for (const tutorial of tutorials) {
      if (!tutorial || !tutorial.promptOnRelease) continue;
      if (hidden.has(tutorial.id)) continue;
      const isMandatory = mandatory.has(tutorial.id);
      if (pass === "mandatory" ? !isMandatory : isMandatory) continue;
      if (!isMandatory && (noPrompt.has(tutorial.id) || suppressed)) continue;
      if (isMandatory && noPrompt.has(tutorial.id)) continue;

      const seen = byId[tutorial.id];
      const versionSeen = Number(seen?.versionSeen) || 0;
      const outdated = versionSeen < (Number(tutorial.version) || 1);
      if (!seen || outdated) return tutorial;
      if (isMandatory && seen.status !== TUTORIAL_STATUS.COMPLETED) return tutorial;
    }
  }

  return null;
};

/** Is this tutorial required (admin config) rather than optional? */
export const isMandatory = (tutorialId, config) =>
  (config?.mandatory || []).includes(tutorialId);

/** Convenience for the Tutorials page badges. */
export const getTutorialProgress = (tutorial, userState) => {
  const seen = userState?.byId?.[tutorial.id];
  if (!seen) return "NEW";
  const versionSeen = Number(seen.versionSeen) || 0;
  if (versionSeen < (Number(tutorial.version) || 1)) return "UPDATED";
  return seen.status === TUTORIAL_STATUS.COMPLETED ? "COMPLETED" : "SKIPPED";
};
